/**
 * HomeView.jsx
 * The app's actual landing tab (separate from the Game Stats tab). Top: "Your
 * Recent Form" — the classic 30-day performance chart, restored from before
 * the Garmin-era Home was removed (git history around commit 92cce53) and
 * adapted for the racket-only, Garmin-free data model. Below: the matches
 * behind that chart, and data-starter chips as quick links into Data Coach.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import supabase from '../supabaseClient';
import { fetchAllPlans } from '../utils/plans';
import { netPlayLabel } from '../utils/sportLabels';
import { toLocalISO, todayLocal, formatDate, formatDateLong } from '../utils/dateLocal';
import useIsMobile from '../utils/useIsMobile';
import { onEnterOrSpace } from '../utils/a11y';
import { opponentLabel, partnerLabel, formatBadge } from '../utils/matchFormat';
import { useSportFiltered } from '../utils/sportScope';
import UpcomingByCompetition from './UpcomingByCompetition';
import { SessionModal, toISO, SeasonGoalManagerModal, CompetitionManagerModal, competitionIcon } from './GamePlanTab';
import LayerSection from './LayerSection';
import CompetitionsSplitTable from './CompetitionsSplitTable';
import EquipmentStatModule from './EquipmentManager';
import {
  ComposedChart, Line, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MATCH_TYPES = ['match', 'competition', 'race_day'];
const TRAINING_TYPES = ['training', 'brick', 'strength', 'recovery', 'other'];
const TRAINING_COLOR = '#7dd3fc';

const C = {
  bg: '#0d1117', card: 'rgba(15,23,42,0.82)', border: 'rgba(255,255,255,0.22)',
  text: '#e2e8f0', muted: '#a8b3bd', accent: '#3dbfb0',
  font: "'Inter', system-ui, sans-serif",
  // ZenAnalytics wordmark blue — same module card treatment as
  // Create & Manage, see GamePlanTab.jsx / docs_zenanalytics/11-MODULE-CENTRIC-REDESIGN-2026-09-29.md.
  logoBlue: '#38bdf8',
  moduleBorder: 'rgba(56,189,248,0.65)',
};

function fmtDate(iso) {
  if (!iso) return '';
  return formatDate(iso);
}

// ── "Your Recent Form" chart — 30 days, win/draw/loss/scheduled dots + a
// performance trend line. Ported from the pre-pivot Home widget (App.js,
// GamePlanTimeline, removed in 92cce53) with the Garmin fields (HR, calories
// from linked activities) dropped and opponent level + star ratings added to
// the tooltip in their place.
function RecentFormChart({ plans, onAddSession }) {
  const todayStr = useMemo(() => todayLocal(), []);

  const dayOff = useCallback((dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T12:00:00');
    const t = new Date(todayStr + 'T12:00:00');
    return Math.round((d - t) / 86400000);
  }, [todayStr]);

  const { matchPoints, trainingPoints, perfLine } = useMemo(() => {
    const toPoint = (p, kind) => {
      const dateStr = p.scheduled_date?.slice(0, 10);
      const x = dayOff(dateStr);
      if (x == null || x < -30 || x > 0) return null;
      const completed = !!(p.result || p.performance_score);
      return {
        x, kind,
        y: completed ? (p.performance_score || 3) : 0.35,
        status: completed ? 'completed' : 'scheduled',
        sport: p.sport || (kind === 'training' ? 'training' : 'match'),
        perf: p.performance_score || null,
        adh: p.game_plan_adherence || null,
        serve: p.serve_rating || null,
        ret: p.return_rating || null,
        forehand: p.forehand_rating || null,
        backhand: p.backhand_rating || null,
        netPlay: p.net_play_rating || null,
        result: p.result || null,
        goal: p.goal || null,
        gamePlan: p.game_plan || null,
        date: dateStr,
        plannedDur: p.estimated_duration || null,
        opponent: p.opponent_name ? opponentLabel(p) : null,
        opponentLevel: p.opponent_level || null,
        notes: p.post_notes || null,
        plan: p,
      };
    };

    const matches = plans.filter(p => MATCH_TYPES.includes(p.type)).map(p => toPoint(p, 'match')).filter(Boolean);
    const trainings = plans.filter(p => TRAINING_TYPES.includes(p.type)).map(p => toPoint(p, 'training')).filter(Boolean);

    const allForLine = [...matches, ...trainings].filter(p => p.perf).sort((a, b) => a.x - b.x);
    const perf = allForLine.map(p => ({ x: p.x, y: p.perf }));
    return {
      matchPoints: matches.sort((a, b) => a.x - b.x),
      trainingPoints: trainings.sort((a, b) => a.x - b.x),
      perfLine: perf,
    };
  }, [plans, dayOff]);

  const fmtTick = useCallback((offset) => {
    const d = new Date(todayStr + 'T12:00:00');
    d.setDate(d.getDate() + offset);
    return `${d.getDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]}`;
  }, [todayStr]);

  const DotShape = useCallback(({ cx, cy, payload }) => {
    if (!cx || !cy || isNaN(cx) || isNaN(cy)) return null;
    const color = payload.kind === 'training' ? TRAINING_COLOR
      : payload.result === 'win' ? '#22c55e'
      : payload.result === 'loss' ? '#ef4444'
      : payload.result === 'draw' ? '#eab308'
      : '#94a3b8';
    const completed = payload.status === 'completed';
    const r = payload.kind === 'training' ? (completed ? 5 : 4) : (completed ? 7 : 5);
    return (
      <circle cx={cx} cy={cy} r={r} fill={completed ? color : 'transparent'} stroke={color} strokeWidth={completed ? 0 : 1.8} />
    );
  }, []);

  const TooltipContent = useCallback(({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const raw = payload.find(p => p.payload?.sport) || payload[0];
    const d = raw?.payload;
    if (!d?.sport) return null;
    const isTraining = d.kind === 'training';
    const color = isTraining ? TRAINING_COLOR
      : d.result === 'win' ? '#22c55e' : d.result === 'loss' ? '#ef4444' : d.result === 'draw' ? '#eab308' : '#94a3b8';
    const stars = (n) => n ? '★'.repeat(n) + '☆'.repeat(5 - n) : null;
    return (
      <div style={{ background: '#0f172a', border: `1px solid ${color}55`, borderRadius: 10, padding: '10px 14px', fontSize: 12, minWidth: 190, maxWidth: 250 }}>
        <div style={{ color, fontWeight: 700, marginBottom: 2, textTransform: 'capitalize' }}>
          {isTraining ? `🏋️ ${d.sport.replace(/_/g, ' ')} training` : (d.opponent ? `vs ${d.opponent}` : d.sport.replace(/_/g, ' '))}
          {!isTraining && d.opponentLevel && <span style={{ color: C.muted, fontWeight: 400 }}> · {d.opponentLevel}</span>}
        </div>
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 8 }}>
          {d.date ? formatDateLong(d.date) : ''} · <span style={{ textTransform: 'capitalize' }}>{d.status}</span>
        </div>
        {d.status === 'completed' ? (<>
          {d.perf && <div style={{ color: '#f59e0b' }}>Performance: {stars(d.perf)}</div>}
          {!isTraining && d.adh && <div style={{ color: C.accent, marginTop: 2 }}>Plan outcome: {stars(d.adh)}</div>}
          {!isTraining && d.serve && <div style={{ color: '#e2e8f0', marginTop: 2 }}>Serve: {stars(d.serve)}</div>}
          {!isTraining && d.ret && <div style={{ color: '#e2e8f0', marginTop: 2 }}>Return: {stars(d.ret)}</div>}
          {!isTraining && d.forehand && <div style={{ color: '#e2e8f0', marginTop: 2 }}>Forehand: {stars(d.forehand)}</div>}
          {!isTraining && d.backhand && <div style={{ color: '#e2e8f0', marginTop: 2 }}>Backhand: {stars(d.backhand)}</div>}
          {!isTraining && d.netPlay && <div style={{ color: '#e2e8f0', marginTop: 2 }}>{netPlayLabel(d.sport)}: {stars(d.netPlay)}</div>}
          {d.notes && (
            <div style={{ color: '#93c5fd', marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6, lineHeight: 1.5 }}>
              📝 {d.notes.length > 120 ? d.notes.slice(0, 120) + '…' : d.notes}
            </div>
          )}
        </>) : (<>
          {d.plannedDur && <div style={{ color: C.muted, marginTop: 2 }}>⏱ Planned: {d.plannedDur} min</div>}
          {d.goal && <div style={{ color: '#e2e8f0', marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6, lineHeight: 1.5 }}>🎯 {d.goal.length > 120 ? d.goal.slice(0, 120) + '…' : d.goal}</div>}
          {d.gamePlan && <div style={{ color: C.muted, marginTop: 4, lineHeight: 1.5 }}>📋 {d.gamePlan.length > 100 ? d.gamePlan.slice(0, 100) + '…' : d.gamePlan}</div>}
        </>)}
      </div>
    );
  }, []);

  if (!matchPoints.length && !trainingPoints.length) return null;

  return (
    <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '16px 16px 12px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ color: C.logoBlue, fontSize: 14, fontWeight: 700 }}>📊 Your Recent Form</div>
          <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>Past 30 days · Today</div>
        </div>
        {onAddSession && (
          <button onClick={onAddSession} style={{ background: '#10b981', border: 'none', borderRadius: 20, padding: '7px 16px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: C.font }}>
            + Add game plan
          </button>
        )}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.14)" />
          <XAxis dataKey="x" type="number" domain={[-30, 0]} ticks={[-28, -21, -14, -7, 0]}
            tickFormatter={fmtTick} tick={{ fill: C.muted, fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis domain={[0, 5.5]} ticks={[1, 2, 3, 4, 5]} tickFormatter={(v) => v >= 1 ? `${v}★` : ''}
            tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} width={46}
            label={{ value: 'Performance', angle: -90, position: 'insideLeft', offset: 12, style: { fill: '#64748b', fontSize: 10, textAnchor: 'middle' } }} />
          <RTooltip content={TooltipContent} cursor={{ stroke: 'rgba(255,255,255,0.15)', strokeWidth: 1 }} />
          <ReferenceLine x={0} stroke="rgba(255,255,255,0.40)" strokeDasharray="4 2" label={{ value: 'Today', fill: C.muted, fontSize: 10, position: 'insideTopLeft' }} />
          <ReferenceLine y={0.35} stroke="rgba(255,255,255,0.10)" />
          <Line data={perfLine} dataKey="y" type="monotone" stroke="#f59e0b" strokeWidth={2} dot={false} activeDot={false} />
          <Scatter data={trainingPoints} dataKey="y" shape={<DotShape />} />
          <Scatter data={matchPoints} dataKey="y" shape={<DotShape />} />
        </ComposedChart>
      </ResponsiveContainer>

      <div style={{ display: 'flex', gap: 12, marginTop: 10, paddingLeft: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.muted }} />
          <span style={{ color: C.muted, fontSize: 10 }}>Completed</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', border: `1.5px solid ${C.muted}` }} />
          <span style={{ color: C.muted, fontSize: 10 }}>Scheduled</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 16, height: 2, background: '#f59e0b', borderRadius: 1 }} />
          <span style={{ color: C.muted, fontSize: 10 }}>Performance ★</span>
        </div>
        <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.12)', margin: '0 2px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
          <span style={{ color: C.muted, fontSize: 10 }}>Win</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#eab308' }} />
          <span style={{ color: C.muted, fontSize: 10 }}>Draw</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
          <span style={{ color: C.muted, fontSize: 10 }}>Loss</span>
        </div>
        <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.12)', margin: '0 2px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: TRAINING_COLOR }} />
          <span style={{ color: C.muted, fontSize: 10 }}>Training session</span>
        </div>
      </div>
    </div>
  );
}

function daysPast(iso) {
  const d = new Date(iso + 'T00:00:00');
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.floor((t - d) / 86400000);
}

// ── Results table — the matches behind the chart above, click to open ────────
function RecentResultsTable({ plans, onOpenPlan }) {
  const todayStr = todayLocal();
  const rows = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const cutoffStr = toLocalISO(cutoff);
    return plans
      .filter(p => MATCH_TYPES.includes(p.type) && p.result
        && (p.scheduled_date || '').slice(0, 10) >= cutoffStr
        && (p.scheduled_date || '').slice(0, 10) <= todayStr)
      .sort((a, b) => (b.scheduled_date || '').localeCompare(a.scheduled_date || ''));
  }, [plans, todayStr]);

  if (rows.length === 0) return null;

  const resultColor = (r) => r === 'win' ? '#22c55e' : r === 'loss' ? '#ef4444' : '#eab308';

  return (
    <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '14px 14px 6px', marginBottom: 16 }}>
      <div style={{ color: C.logoBlue, fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Results — last 30 days</div>
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {rows.map(p => (
          <div key={p.id} onClick={() => onOpenPlan(p)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => onOpenPlan(p))} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 2px',
            borderTop: `1px solid rgba(255,255,255,0.06)`, cursor: 'pointer',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: resultColor(p.result), flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: C.text, fontSize: 12.5, fontWeight: 600 }}>
              {p.opponent_name ? `vs ${opponentLabel(p)}` : (p.goal || p.sport)}
            </div>
            <div style={{ color: C.muted, fontSize: 10.5, flexShrink: 0, textAlign: 'right' }}>
              {fmtDate(p.scheduled_date)}
              {/* "Ngd ago" next to the date -- useful at a glance when
                  scanning back through match history. Feedback 2026-11-02. */}
              <span style={{ color: C.dim, fontSize: 9.5, marginLeft: 4 }}>
                {(() => {
                  const d = daysPast((p.scheduled_date || '').slice(0, 10));
                  return d === 0 ? '(today)' : `(${d}d ago)`;
                })()}
              </span>
            </div>
            {p.performance_score && <div style={{ color: '#f59e0b', fontSize: 11, flexShrink: 0 }}>{p.performance_score}★</div>}
            <div style={{ color: resultColor(p.result), fontSize: 10, fontWeight: 800, textTransform: 'uppercase', flexShrink: 0, width: 34, textAlign: 'right' }}>{p.result}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Development areas — data-analysis chat starters, computed from the
// athlete's own actual data rather than a fixed pool.
// Rewritten 2026-10-30 (user feedback) two ways at once:
//   1. Dynamic, not static — each starter only appears if the athlete
//      actually has the data behind it (e.g. a correlation question only
//      shows up once there are enough rated matches for a correlation to
//      mean anything; a head-to-head question names the athlete's real
//      most-played opponent instead of a generic placeholder).
//   2. Data-analysis framing, not coaching framing — "Which stroke is
//      costing me the most right now?" reads as technique advice-seeking,
//      which isn't what this card is for (it's an entry point to Data
//      Coach the analyst, not a swap-in for a real coach, per the
//      2026-10-25 product direction already noted below). Replaced with
//      descriptive/statistical phrasing: patterns, correlations, spread,
//      trends — what the data shows, not what's "wrong".
// "Summarize my season so far" always qualifies (even a brand-new athlete
// with zero matches has *a* season to summarize), so there's always at
// least one starter to show.
//
// Third tier added 2026-10-30 (user feedback): a brand-new athlete with
// under _NEW_USER_THRESHOLD game plans logged doesn't have enough data for
// ANY of the interpretation questions below to mean much yet — suggesting
// them anyway would just produce a thin or apologetic answer. Below that
// threshold the starters switch to onboarding/tutorial questions instead,
// teaching the athlete what Data Coach can do and how to get the most out
// of it as they log more, rather than asking it to interpret data that
// doesn't exist yet.
const _NEW_USER_THRESHOLD = 6; // total game plans (any type), not just matches
const _ONBOARDING_STARTERS = [
  "What can you help me with?",
  "What do you already know about me so far?",
  "What should I log to get the most useful answers from you?",
  "How should I use you as I log more sessions?",
];
const _MIN_FOR_CORRELATION = 5; // matches data_coach_routes.py's _MIN_CORRELATION_N

function _buildDataStarters(plans) {
  if ((plans || []).length < _NEW_USER_THRESHOLD) return _ONBOARDING_STARTERS;

  const matches = (plans || []).filter(p => ['match', 'competition', 'race_day'].includes(p.type) && p.result);
  const withPerf     = matches.filter(p => p.performance_score);
  const withForehand = matches.filter(p => p.forehand_rating);
  const withHR       = matches.filter(p => p.avg_heart_rate);
  const losses       = matches.filter(p => p.result === 'loss');

  const oppCounts = {};
  matches.forEach(p => { if (p.opponent_name) oppCounts[p.opponent_name] = (oppCounts[p.opponent_name] || 0) + 1; });
  const topOpponent = Object.entries(oppCounts).sort((a, b) => b[1] - a[1])[0];

  const starters = ["Summarize my season so far"];
  if (matches.length >= _MIN_FOR_CORRELATION) starters.push("What patterns show up across my recent matches?");
  if (withPerf.length >= _MIN_FOR_CORRELATION) starters.push("What's the strongest correlation in my match data?");
  if (withForehand.length >= 2) starters.push("How has my forehand rating trended over time?");
  if (topOpponent && topOpponent[1] >= 2) starters.push(`What's my head-to-head record against ${topOpponent[0]}?`);
  if (losses.length >= 2) starters.push("What do my losses have in common?");
  if (withPerf.length >= 3) starters.push("Where's the biggest gap between my best- and worst-rated areas?");
  if (withHR.length >= _MIN_FOR_CORRELATION) starters.push("How does my heart rate relate to how I perform?");

  return starters;
}

function _pickStarters(pool, n) {
  const copy = [...pool];
  const out = [];
  while (out.length < n && copy.length) {
    out.push(copy.splice(Math.floor(Math.random() * copy.length), 1)[0]);
  }
  return out;
}

function DevelopmentAreas({ onGoToCoach, plans }) {
  const pool = useMemo(() => _buildDataStarters(plans), [plans]);
  // Reshuffled only when the eligible pool itself actually changes (e.g. a
  // sport switch, or crossing a data threshold) — keyed on its contents,
  // not re-picked on every unrelated re-render, which would otherwise
  // flicker the chips to a new random set each time Home updates.
  const poolKey = pool.join('|');
  const starters = useMemo(() => _pickStarters(pool, Math.min(4, pool.length)), [poolKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '16px 16px', marginBottom: 16 }}>
      <div style={{ color: C.logoBlue, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
        💬 Chat with data coach
      </div>
      <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        📊 Ask about your data
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {starters.map((a, i) => (
          <button key={i} onClick={onGoToCoach} style={{
            background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.35)',
            color: '#c4b5fd', borderRadius: 20, padding: '6px 13px', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: C.font,
          }}>{a} →</button>
        ))}
      </div>
    </div>
  );
}

// ── Summary — past 30 days. Skill breakdown only (serve/return/forehand/
// backhand/net play), same tile style as the all-time breakdown in Game
// Stats, but scoped to the same 30-day window as "Your Recent Form" below —
// same window, same story, instead of a match-count window that could cover
// a different span than the chart. First thing the athlete sees on Home.
function SummaryLast10({ plans }) {
  const { windowMatches, trainingCount } = useMemo(() => {
    const today = new Date();
    const todayStr = toLocalISO(today);
    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() - 30);
    const cutoffStr = toLocalISO(cutoff);
    const inWindow = (p) => (p.scheduled_date || '').slice(0, 10) >= cutoffStr && (p.scheduled_date || '').slice(0, 10) <= todayStr;
    const matches = plans.filter(p => MATCH_TYPES.includes(p.type) && p.result && inWindow(p))
      .sort((a, b) => (b.scheduled_date || '').localeCompare(a.scheduled_date || ''));
    const trainings = plans.filter(p => TRAINING_TYPES.includes(p.type) && inWindow(p));
    return { windowMatches: matches, trainingCount: trainings.length };
  }, [plans]);

  const avgOf = (field) => {
    const vals = windowMatches.filter(p => p[field]).map(p => p[field]);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };

  if (windowMatches.length === 0 && trainingCount === 0) return null;

  const breakdownSport = windowMatches[0]?.sport;
  const tiles = [
    { label: 'Serve',    value: avgOf('serve_rating') && `${avgOf('serve_rating')}★` },
    { label: 'Return',   value: avgOf('return_rating') && `${avgOf('return_rating')}★` },
    { label: 'Forehand', value: avgOf('forehand_rating') && `${avgOf('forehand_rating')}★` },
    { label: 'Backhand', value: avgOf('backhand_rating') && `${avgOf('backhand_rating')}★` },
    { label: netPlayLabel(breakdownSport), value: avgOf('net_play_rating') && `${avgOf('net_play_rating')}★` },
    { label: 'Training sessions logged', value: trainingCount || null, accent: TRAINING_COLOR },
  ];

  return (
    <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '14px 14px', marginBottom: 16 }}>
      <div style={{ color: C.logoBlue, fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
        Summary — Past 30 days <span style={{ color: C.muted, fontWeight: 400 }}>({windowMatches.length} match{windowMatches.length !== 1 ? 'es' : ''})</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: C.border, borderRadius: 10, overflow: 'hidden' }}>
        {tiles.map(t => (
          <div key={t.label} style={{ background: '#0f172a', padding: '11px 8px', textAlign: 'center' }}>
            <div style={{ color: t.accent || C.accent, fontSize: 17, fontWeight: 800 }}>{t.value ?? '—'}</div>
            <div style={{ color: C.muted, fontSize: 10.5, marginTop: 3 }}>{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Top-5 heads-up — the athlete's 5 most-played opponents, same W/D/L +
// avg perf/plan summary as the Game Stats "Heads Up" tab, collapsed to rows
// that expand into that opponent's match history (click through to the
// underlying game plan). Placed last on Home. When an opponent's expanded
// history is longer than 5 matches, the list scrolls instead of growing the
// page — same pattern as the results table above.
function TopHeadsUp({ userId, demoMode, plans, onOpenPlan }) {
  const [opponents, setOpponents] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (demoMode) {
      fetch(`${API}/game-plans/demo/opponents`)
        .then(r => r.json())
        .then(d => setOpponents(d.opponents || []))
        .catch(e => console.error('[TopHeadsUp] demo opponents load failed:', e));
      return;
    }
    if (!userId) return;
    supabase.from('opponents').select('*').eq('user_id', userId).order('name')
      .then(({ data }) => setOpponents(data || []));
  }, [userId, demoMode]);

  const todayStr = todayLocal();

  const rows = useMemo(() => {
    const matchPlans = plans.filter(p => MATCH_TYPES.includes(p.type) && p.opponent_id && (p.scheduled_date || '') <= todayStr);
    const byOpponent = {};
    matchPlans.forEach(p => {
      if (!byOpponent[p.opponent_id]) byOpponent[p.opponent_id] = [];
      byOpponent[p.opponent_id].push(p);
      if (p.opponent2_id && p.opponent2_id !== p.opponent_id) {
        if (!byOpponent[p.opponent2_id]) byOpponent[p.opponent2_id] = [];
        byOpponent[p.opponent2_id].push(p);
      }
    });
    return opponents
      .filter(o => byOpponent[o.id]?.length > 0)
      .map(o => {
        const ps = byOpponent[o.id];
        const w = ps.filter(p => p.result === 'win').length;
        const d = ps.filter(p => p.result === 'draw').length;
        const l = ps.filter(p => p.result === 'loss').length;
        const perfScores = ps.filter(p => p.performance_score).map(p => p.performance_score);
        const planScores = ps.filter(p => p.game_plan_adherence).map(p => p.game_plan_adherence);
        const avgPerf = perfScores.length ? (perfScores.reduce((a, b) => a + b, 0) / perfScores.length).toFixed(1) : null;
        const avgPlan = planScores.length ? (planScores.reduce((a, b) => a + b, 0) / planScores.length).toFixed(1) : null;
        return { opponent: o, sessions: ps.sort((a, b) => (b.scheduled_date || '').localeCompare(a.scheduled_date || '')), w, d, l, avgPerf, avgPlan };
      })
      .sort((a, b) => b.sessions.length - a.sessions.length)
      .slice(0, 5);
  }, [opponents, plans, todayStr]);

  if (rows.length === 0) return null;

  const resultColor = r => r === 'win' ? '#22c55e' : r === 'loss' ? '#ef4444' : r === 'draw' ? '#eab308' : C.muted;
  const resultLabel = r => r === 'win' ? 'W' : r === 'draw' ? 'D' : r === 'loss' ? 'L' : '—';

  return (
    <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '14px 14px', marginBottom: 16 }}>
      <div style={{ color: C.logoBlue, fontSize: 14, fontWeight: 700, marginBottom: 2 }}>🥊 Top-5 Heads-Up</div>
      <div style={{ color: C.muted, fontSize: 10.5, marginBottom: 10 }}>Your most-played opponents · click a row for match history</div>
      {rows.map(({ opponent, sessions, w, d, l, avgPerf, avgPlan }) => {
        const isOpen = expanded === opponent.id;
        return (
          <div key={opponent.id} style={{ marginBottom: 6, borderRadius: 10, overflow: 'hidden', border: `1px solid ${isOpen ? 'rgba(61,191,176,0.35)' : C.border}` }}>
            <div
              onClick={() => setExpanded(isOpen ? null : opponent.id)}
              style={{
                display: 'grid',
                // Mobile drops the "plan" column (perf ★ already covers the
                // headline number) and narrows W/D/L so the opponent name
                // isn't squeezed to near-nothing on a phone — see production
                // readiness audit, 2026-10-20.
                gridTemplateColumns: isMobile ? '1fr 26px 26px 26px 42px 20px' : '1fr 34px 34px 34px 50px 50px 22px',
                alignItems: 'center', gap: isMobile ? 2 : 4, padding: '10px 12px', cursor: 'pointer',
                background: isOpen ? 'rgba(61,191,176,0.08)' : 'rgba(255,255,255,0.03)',
              }}
            >
              <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span style={{ color: C.text, fontWeight: 600, fontSize: 12.5 }}>{opponent.name}</span>
                <span style={{ color: C.muted, fontSize: 10.5, marginLeft: 6 }}>{sessions.length}g</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#22c55e', fontWeight: 800, fontSize: 13 }}>{w}</div>
                <div style={{ color: C.muted, fontSize: 8.5 }}>W</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#eab308', fontWeight: 800, fontSize: 13 }}>{d}</div>
                <div style={{ color: C.muted, fontSize: 8.5 }}>D</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#ef4444', fontWeight: 800, fontSize: 13 }}>{l}</div>
                <div style={{ color: C.muted, fontSize: 8.5 }}>L</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: C.accent, fontSize: 11 }}>{avgPerf ? `${avgPerf}★` : '—'}</div>
                <div style={{ color: C.muted, fontSize: 8.5 }}>perf</div>
              </div>
              {!isMobile && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#a78bfa', fontSize: 11 }}>{avgPlan ? `${avgPlan}★` : '—'}</div>
                  <div style={{ color: C.muted, fontSize: 8.5 }}>plan</div>
                </div>
              )}
              <div style={{ color: C.muted, fontSize: 12, textAlign: 'center' }}>{isOpen ? '▲' : '▼'}</div>
            </div>
            {isOpen && (
              <div style={{ background: 'rgba(0,0,0,0.25)', padding: '4px 12px', maxHeight: 220, overflowY: 'auto' }}>
                {sessions.map(s => (
                  <div key={s.id} onClick={() => onOpenPlan(s)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => onOpenPlan(s))} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0',
                    borderTop: `1px solid ${C.border}`, fontSize: 12, cursor: 'pointer',
                  }}>
                    <div style={{ color: C.muted, fontSize: 11, flexShrink: 0, width: 50 }}>{fmtDate(s.scheduled_date)}</div>
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.accent, textDecoration: 'underline', textDecorationColor: 'rgba(52,211,153,0.35)' }}>
                      {formatBadge(s) && <span style={{ color: C.muted, fontWeight: 700, marginRight: 4 }}>{formatBadge(s)}</span>}
                      {s.goal || s.type}
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 12.5, color: resultColor(s.result), flexShrink: 0 }}>{resultLabel(s.result)}</div>
                    <div style={{ color: '#f59e0b', fontSize: 10.5, flexShrink: 0, width: 30, textAlign: 'right' }}>{s.performance_score ? `${s.performance_score}★` : '—'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Season summary — top-of-hierarchy module (season -> competitions ->
// matches, see docs_zenanalytics/PRODUCT-GUIDELINES.md §1). A quick glance
// at where the season stands: the goal, and each linked competition's
// status, without having to go into Create & Manage first. Clicking a
// competition deep-links straight into its edit form there. See
// docs_zenanalytics/14-NAVIGATION-SHORTCUTS-2026-10-01.md.
function compIconHV(type) {
  return type === 'league' ? '🏅' : type === 'tournament' ? '🏆' : '🎯';
}

function SeasonSummary({ userId, demoMode, plans, activeSport, authSession }) {
  const [seasonGoal, setSeasonGoal] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [showSeasonManager, setShowSeasonManager] = useState(false);
  const [showCompManager, setShowCompManager] = useState(false); // false | true | a competition object

  // Lives entirely on Home now — managing the season and its competitions
  // used to require switching to Create & Manage first just to click
  // "Manage". Per user feedback 2026-10-02: no reason to make someone leave
  // the page they're already looking at the summary on.
  // `activeToken` guards against the same stale-response race documented in
  // WhatsNext below — manual calls (onRefresh) pass no token and always
  // apply, since they're not part of a sport-switch race.
  const loadSeasonGoal = (token) => {
    if (demoMode) {
      fetch(`${API}/game-plans/demo/season-goal`)
        .then(r => r.json())
        .then(d => { if (token && token.cancelled) return; setSeasonGoal(d.season_goal || null); setMilestones(d.milestones || []); })
        .catch(e => console.error('[SeasonSummary] demo season-goal load failed:', e));
      return;
    }
    if (!userId) return;
    let sq = supabase.from('season_goals').select('*').eq('user_id', userId).eq('status', 'active');
    if (activeSport) sq = sq.eq('sport', activeSport);
    sq.order('created_at', { ascending: false }).limit(1).then(({ data }) => {
        if (token && token.cancelled) return;
        const sg = (data || [])[0] || null;
        setSeasonGoal(sg);
        if (sg) {
          supabase.from('milestones').select('*').eq('season_goal_id', sg.id)
            .order('created_at').then(({ data: ms }) => { if (!(token && token.cancelled)) setMilestones(ms || []); });
        } else {
          setMilestones([]);
        }
      });
  };

  const loadCompetitions = (token) => {
    if (demoMode) {
      fetch(`${API}/game-plans/demo/competitions`)
        .then(r => r.json())
        .then(d => { if (!(token && token.cancelled)) setCompetitions(d.competitions || []); })
        .catch(e => console.error("[SeasonSummary] demo competitions load failed:", e));
      return;
    }
    if (!userId) return;
    // All statuses here (not just active) -- a completed competition still
    // belongs to the season and should show its final numbers.
    let cq = supabase.from('competitions').select('*').eq('user_id', userId);
    if (activeSport) cq = cq.eq('sport', activeSport);
    cq.then(({ data }) => { if (!(token && token.cancelled)) setCompetitions(data || []); });
  };

  useEffect(() => {
    const token = { cancelled: false };
    loadSeasonGoal(token);
    return () => { token.cancelled = true; };
  }, [userId, demoMode, activeSport]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const token = { cancelled: false };
    loadCompetitions(token);
    return () => { token.cancelled = true; };
  }, [userId, demoMode, activeSport]); // eslint-disable-line react-hooks/exhaustive-deps

  const seasonComps = competitions.filter(c => seasonGoal && c.season_goal_id === seasonGoal.id);

  // No active season (or one with nothing linked yet) — a short hint
  // instead of rendering nothing, so a LayerSection wrapping this never
  // looks like a broken empty collapsible with no explanation inside.
  if (!seasonGoal || seasonComps.length === 0) {
    return (
      <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ color: C.muted, fontSize: 12.5, fontStyle: 'italic' }}>
          No active season set — name one and link competitions to it in Create & Manage.
        </div>
      </div>
    );
  }

  return (
    <>
    <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '16px 16px 12px', marginBottom: 16 }}>
      {/* No repeated "Season" label here (user feedback, 2026-10-30) — the
          LayerSection wrapping this card already shows that title; this
          row keeps only the Manage control. */}
      {!demoMode && userId && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
          <button onClick={() => setShowSeasonManager(true)} style={{
            background: 'rgba(16,185,129,0.12)', border: `1px solid ${C.accent}`, borderRadius: 6,
            color: C.accent, fontSize: 11, fontWeight: 700, padding: '4px 10px', cursor: 'pointer', fontFamily: C.font,
          }}>Manage →</button>
        </div>
      )}
      <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>
        {seasonGoal.name || 'Unnamed season'}
      </div>
      {seasonGoal.goal && (
        <div style={{ color: C.muted, fontSize: 11, marginTop: 2, marginBottom: 10 }}>🎯 {seasonGoal.goal}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: seasonGoal.goal ? 0 : 10 }}>
        {seasonComps.map(c => {
          const compPlans = plans.filter(p => p.competition_id === c.id);
          const matches = compPlans.filter(p => ['match', 'competition'].includes(p.type) && p.result);
          const wins = matches.filter(p => p.result === 'win').length;
          const total = c.format_meta?.total_matches_planned || compPlans.length;
          const avgPerfVals = matches.filter(p => p.performance_score).map(p => p.performance_score);
          const avgPerf = avgPerfVals.length ? (avgPerfVals.reduce((a, b) => a + b, 0) / avgPerfVals.length).toFixed(1) : null;
          const isActive = c.status !== 'archived';
          return (
            <div key={c.id} onClick={() => !demoMode && userId && setShowCompManager(c)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => !demoMode && userId && setShowCompManager(c))} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 8,
              padding: '8px 10px', cursor: (!demoMode && userId) ? 'pointer' : 'default',
            }}>
              <span style={{ color: C.text, fontSize: 12.5, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {compIconHV(c.type)} {c.name}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, fontSize: 11 }}>
                {isActive ? (
                  <>
                    <span style={{ color: C.muted }}>{matches.length}/{total} played</span>
                    <span style={{ color: '#f59e0b', fontWeight: 700, background: 'rgba(245,158,11,0.15)', borderRadius: 10, padding: '2px 8px' }}>Active</span>
                  </>
                ) : (
                  <>
                    <span style={{ color: C.muted }}>
                      {matches.length ? `${Math.round(wins / matches.length * 100)}% win` : '—'}{avgPerf ? ` · ${avgPerf}★` : ''}
                    </span>
                    <span style={{ color: '#22c55e', fontWeight: 700, background: 'rgba(34,197,94,0.15)', borderRadius: 10, padding: '2px 8px' }}>Completed</span>
                  </>
                )}
                {!demoMode && userId && <span style={{ color: C.muted }}>→</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
    {/* Rendered as a sibling of the card, not inside it: the card's own
        backdropFilter creates a new stacking context, which traps a
        position:fixed modal behind whatever module comes right after it in
        the DOM (e.g. "Chat with coach") regardless of z-index. See user
        feedback 2026-10-02. */}
    {showSeasonManager && (
      <SeasonGoalManagerModal
        userId={userId}
        seasonGoal={seasonGoal}
        milestones={milestones}
        competitions={competitions}
        activeSport={activeSport}
        onRefresh={async () => { loadSeasonGoal(); loadCompetitions(); }}
        onClose={() => setShowSeasonManager(false)}
      />
    )}
    {showCompManager && (
      <CompetitionManagerModal
        userId={userId}
        competitions={competitions}
        plans={plans}
        activeSport={activeSport}
        authSession={authSession}
        onRefresh={loadCompetitions}
        onClose={() => setShowCompManager(false)}
        initialEditing={typeof showCompManager === 'object' ? showCompManager : null}
      />
    )}
    </>
  );
}

// ── Competitions table — the same summary + edit access Create & Manage has,
// surfaced here too so reviewing "how competitions are going" doesn't
// require leaving Home. See user feedback 2026-10-02.
function HomeCompetitions({ userId, demoMode, plans, activeSport, authSession }) {
  const [competitions, setCompetitions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'all' | 'archived'
  const [showCompManager, setShowCompManager] = useState(false); // false | true | a competition object

  const loadCompetitions = (token) => {
    if (demoMode) {
      fetch(`${API}/game-plans/demo/competitions`)
        .then(r => r.json())
        .then(d => { if (!(token && token.cancelled)) setCompetitions(d.competitions || []); })
        .catch(e => console.error("[HomeCompetitions] demo competitions load failed:", e));
      return;
    }
    if (!userId) return;
    let q = supabase.from('competitions').select('*').eq('user_id', userId);
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('start_date', { ascending: false })
      .then(({ data }) => { if (!(token && token.cancelled)) setCompetitions(data || []); });
  };

  useEffect(() => {
    const token = { cancelled: false };
    loadCompetitions(token);
    return () => { token.cancelled = true; };
  }, [userId, demoMode, activeSport]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!competitions.length) return null;
  const filtered = competitions.filter(c =>
    statusFilter === 'all' ? true : statusFilter === 'active' ? c.status !== 'archived' : c.status === 'archived'
  );
  const canManage = !demoMode && userId;

  return (
    <>
    <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '16px 16px 12px', marginBottom: 16 }}>
      {/* No repeated "Competitions" label here (user feedback, 2026-10-30)
          — the LayerSection wrapping this card already shows that title;
          this row keeps only the actionable controls. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{
          background: 'rgba(255,255,255,0.09)', border: `1px solid ${C.border}`, borderRadius: 6,
          color: C.text, fontSize: 11.5, padding: '4px 8px', fontFamily: C.font, cursor: 'pointer',
        }}>
          <option value="active">Active</option>
          <option value="all">All</option>
          <option value="archived">Archived</option>
        </select>
        {canManage && (
          <button onClick={() => setShowCompManager(true)} style={{
            background: 'rgba(16,185,129,0.12)', border: `1px solid ${C.accent}`, borderRadius: 6,
            color: C.accent, fontSize: 11.5, fontWeight: 700, padding: '5px 12px', cursor: 'pointer', fontFamily: C.font,
          }}>Manage →</button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13, fontStyle: 'italic' }}>No {statusFilter} competitions.</div>
      ) : (
        <CompetitionsSplitTable
          competitions={filtered}
          plans={plans}
          onRowClick={setShowCompManager}
          canManage={canManage}
        />
      )}
    </div>
    {showCompManager && (
      <CompetitionManagerModal
        userId={userId}
        competitions={competitions}
        plans={plans}
        activeSport={activeSport}
        authSession={authSession}
        onRefresh={loadCompetitions}
        onClose={() => setShowCompManager(false)}
        initialEditing={typeof showCompManager === 'object' ? showCompManager : null}
      />
    )}
    </>
  );
}

// ── What's Next — forward-looking counterpart to "Your Recent Form" below.
// Same 3-tier hierarchy the app already stores (Season Goal -> Competition ->
// Game Plan) but never surfaced as one: next 14 days, grouped by competition,
// so an athlete juggling several concurrent leagues/tournaments sees what's
// coming up in each instead of a flat date list. See
// docs_zenanalytics/05-SCHEDULE-MILESTONES-SPEC-2026-09-25.md.
const WHATS_NEXT_HORIZON_DAYS = 14;

function WhatsNext({ userId, demoMode, plans, activeSport, onOpenPlan, onAddSession, needsResult = [] }) {
  const [competitions, setCompetitions] = useState([]);
  // Structured season goal — see docs_zenanalytics/10-SEASON-GOALS-MILESTONES-2026-09-29.md.
  // Replaces the old free-text user_profiles.goals read.
  const [seasonGoal, setSeasonGoal] = useState(null);

  useEffect(() => {
    // Guard against a race: switching activeSport quickly fires a new
    // request before the previous (now-wrong-sport) one resolves — without
    // this, a slower stale response can overwrite the correct one. Found
    // 2026-10-18 testing all 6 sports: table_tennis's season goal briefly
    // appeared under squash. See PRODUCT-GUIDELINES.md §5.
    let cancelled = false;
    if (demoMode) {
      fetch(`${API}/game-plans/demo/competitions`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setCompetitions(d.competitions || []); })
        .catch(e => console.error("[WhatsNext] demo competitions load failed:", e));
      return () => { cancelled = true; };
    }
    if (!userId) return;
    let q = supabase.from('competitions').select('*').eq('user_id', userId).eq('status', 'active');
    if (activeSport) q = q.eq('sport', activeSport);
    q.then(({ data }) => { if (!cancelled) setCompetitions(data || []); });
    return () => { cancelled = true; };
  }, [userId, demoMode, activeSport]);

  useEffect(() => {
    let cancelled = false;
    if (demoMode) {
      fetch(`${API}/game-plans/demo/season-goal`)
        .then(r => r.json())
        .then(d => { if (!cancelled) setSeasonGoal(d.season_goal || null); })
        .catch(e => console.error("[WhatsNext] demo season-goal load failed:", e));
      return () => { cancelled = true; };
    }
    if (!userId) return;
    let sq = supabase.from('season_goals').select('*').eq('user_id', userId).eq('status', 'active');
    if (activeSport) sq = sq.eq('sport', activeSport);
    sq.order('created_at', { ascending: false }).limit(1)
      .then(({ data }) => { if (!cancelled) setSeasonGoal((data || [])[0] || null); });
    return () => { cancelled = true; };
  }, [userId, demoMode, activeSport]);

  return (
    <div style={{ background: C.card, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 14, padding: '16px 16px 12px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: seasonGoal?.goal ? 2 : 10 }}>
        <div style={{ color: C.logoBlue, fontSize: 14, fontWeight: 700 }}>📅 What's Next</div>
        {onAddSession && (
          <button onClick={onAddSession} style={{ background: '#10b981', border: 'none', borderRadius: 20, padding: '7px 16px', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: C.font }}>
            + Add game plan
          </button>
        )}
      </div>
      {seasonGoal?.goal && (
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 12 }}>🏆 Goal: {seasonGoal.goal}</div>
      )}
      {/* "Waiting for result" — sessions scheduled today or earlier with no
          result logged yet. Moved here from Create & Manage's "N sessions
          need a result" banner (user feedback, 2026-10-28): this is the
          single most actionable thing on the page, and it was previously
          hidden on the view people don't land on daily. Renders nothing
          when empty — no manufactured content. */}
      {needsResult.length > 0 && (
        <div style={{
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.40)',
          borderRadius: 10, padding: '9px 14px', marginBottom: 12,
        }}>
          <div style={{ color: '#f59e0b', fontSize: 12.5, fontWeight: 700 }}>
            ⏳ Waiting for result — {needsResult.length} session{needsResult.length !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
            {needsResult.slice(0, 3).map(p => (
              <div key={p.id} onClick={() => onOpenPlan(p)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => onOpenPlan(p))} style={{
                color: C.text, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline',
                textDecorationColor: 'rgba(245,158,11,0.35)',
              }}>
                {formatDate(p.scheduled_date?.slice(0, 10))} · {p.goal || p.type}
              </div>
            ))}
            {needsResult.length > 3 && (
              <div style={{ color: C.muted, fontSize: 11.5 }}>+{needsResult.length - 3} more</div>
            )}
          </div>
        </div>
      )}
      <UpcomingByCompetition
        plans={plans}
        competitions={competitions}
        horizonDays={WHATS_NEXT_HORIZON_DAYS}
        previewCount={2}
        onOpenPlan={onOpenPlan}
        emptyHint="Nothing planned in the next 14 days — add your next game plan."
      />
    </div>
  );
}

export default function HomeView({ session, demoMode = false, activeSport, onOpenPlan, onGoToCoach, onAddSession, onOpenCompetition }) {
  const userId = session?.user?.id;
  const [plans, setPlans] = useState([]);
  // Full isolation between sports (2026-10-18, PRODUCT-GUIDELINES.md §5).
  const visiblePlans = useSportFiltered(plans, activeSport);
  const [loading, setLoading] = useState(true);
  // "+ Add game plan" used to switch tabs to Create & Manage before opening
  // the form — per user feedback, it should open right here on Home instead,
  // so a quick entry doesn't require leaving the page you're already on.
  const [showAddModal, setShowAddModal] = useState(false);
  const openAddHere = () => setShowAddModal(true);
  // Same fix, extended 2026-10-25 to opening an EXISTING plan from Home
  // (What's Next / Recent results / Heads Up): it used to call the parent's
  // onOpenPlan, which switches to Create & Manage and leaves you there
  // after saving — there was nothing to switch back. Editing in place here
  // keeps you on Home, matching where you started.
  const [editSession, setEditSession] = useState(null);
  // Demo mode has no real userId/authSession to save against, and is
  // read-only anyway, so it keeps the old switch-to-Create&Manage behavior.
  const openPlanHere = demoMode ? onOpenPlan : (plan) => setEditSession(plan);

  // "Waiting for result" — same definition Create & Manage's old "N
  // sessions need a result" banner used (GamePlanTab.jsx), now computed
  // here too since that banner moved onto Home's What's Next card (user
  // feedback, 2026-10-28).
  const todayISOHome = toISO(new Date());
  const needsResult = useMemo(() =>
    visiblePlans
      .filter(p => p.scheduled_date?.slice(0, 10) <= todayISOHome && !p.result && !p.performance_score)
      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date)),
    [visiblePlans, todayISOHome]
  );

  const loadPlans = useCallback(async () => {
    if (demoMode) {
      try {
        const res = await fetch(`${API}/game-plans/demo`);
        const data = res.ok ? await res.json() : { plans: [] };
        setPlans(data.plans || []);
      } catch (e) {
        console.error('[HomeView] loadPlans demo exception:', e);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!userId) { setLoading(false); return; }
    try {
      const { data, error } = await fetchAllPlans(userId);
      if (error) console.error('[HomeView] loadPlans error:', error);
      setPlans(data || []);
    } catch (e) {
      console.error('[HomeView] loadPlans exception:', e);
    } finally {
      setLoading(false);
    }
  }, [userId, demoMode]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  if (loading) {
    return <div style={{ padding: 24, color: C.muted, fontFamily: C.font }}>Loading…</div>;
  }

  return (
    <div style={{ padding: '12px 12px 24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Section order (2026-10-28 restructure) is by DAILY USAGE FREQUENCY,
          not hierarchy: Matches (logging a plan or a result is the single
          most common action here) -> Competitions -> Season -> Other stats
          -> the Data Coach CTA. Deliberately different from Create &
          Manage's Season -> Competitions -> Matches order, which is
          top-down by structural hierarchy instead — that's the view where
          the hierarchy itself gets built. See
          docs_zenanalytics/25-STRUCTURE-AND-CAREER-RECORD-PLANNING-2026-10-28.md. */}
      <LayerSection id="matches" userId={userId} icon="🎾" title="Matches">
        <WhatsNext userId={userId} demoMode={demoMode} plans={visiblePlans} activeSport={activeSport} onOpenPlan={openPlanHere} onAddSession={onAddSession && openAddHere} needsResult={needsResult} />
        {/* "+ Add game plan" lives only on What's Next now — having it twice
            on the page was redundant (user feedback 2026-10-02). Internal
            order — Recent form chart -> Results -> 30-day summary — is a
            narrative arc: trend first, the matches behind that trend next,
            the number that summarizes both last (user feedback, 2026-10-28). */}
        <RecentFormChart plans={visiblePlans} />
        <RecentResultsTable plans={visiblePlans} onOpenPlan={openPlanHere} />
        <SummaryLast10 plans={visiblePlans} />
      </LayerSection>

      <LayerSection id="competitions" userId={userId} icon="🏆" title="Competitions">
        <HomeCompetitions userId={userId} demoMode={demoMode} plans={visiblePlans} activeSport={activeSport} authSession={session} />
      </LayerSection>

      <LayerSection id="season" userId={userId} icon="🏆" title="Season">
        <SeasonSummary userId={userId} demoMode={demoMode} plans={visiblePlans} activeSport={activeSport} authSession={session} />
      </LayerSection>

      {/* "Other stats" — Equipment usage, Top-5 opponents, and the Data
          Coach CTA, all folded into one collapsible section (user
          feedback, 2026-10-30): keeping the chat CTA outside meant
          collapsing this section left it dangling open on its own below
          an empty-looking footer — one toggle controlling all three is a
          clearer mental model than two overlapping ones, even though the
          CTA itself isn't a "stat" the way the other two are. */}
      <LayerSection id="other-stats" userId={userId} icon="📦" title="Other stats">
        <EquipmentStatModule userId={userId} activeSport={activeSport} readOnly={demoMode} demoMode={demoMode} plans={visiblePlans} />
        <TopHeadsUp userId={userId} demoMode={demoMode} plans={visiblePlans} onOpenPlan={openPlanHere} />
        <DevelopmentAreas onGoToCoach={onGoToCoach} plans={visiblePlans} />
      </LayerSection>

      {showAddModal && (
        <SessionModal
          session={null}
          date={toISO(new Date())}
          userId={userId}
          authSession={session}
          activeSport={activeSport}
          onSaved={() => { setShowAddModal(false); loadPlans(); }}
          onClose={() => setShowAddModal(false)}
        />
      )}
      {editSession && (
        <SessionModal
          session={editSession}
          date={editSession.scheduled_date?.slice(0, 10)}
          userId={userId}
          authSession={session}
          activeSport={activeSport}
          onSaved={() => { setEditSession(null); loadPlans(); }}
          onClose={() => setEditSession(null)}
        />
      )}
    </div>
  );
}
